//@ts-check

import styled from '@emotion/styled';
import { Dropdown, Menu, Row } from 'antd';
import {
  handleToastError,
  removeNullUndefined,
  transformedArray,
} from 'helpers';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, Filter } from 'react-feather';
import { useSelector } from 'react-redux';
import { Badge, Form } from 'reactstrap';
import { getSelectedCentre } from 'store/master';
import ThemeButton from 'components/AntdFormInputs/theme-button';
import { Controller, useForm } from 'react-hook-form';
import { formatHHmmDDMMYYYY } from 'helpers/format-time';
import moment from 'moment';
import classnames from 'classnames';
import { MdFilterList } from 'react-icons/md';
import { getColor } from 'store/settings';

/**
 * @typedef {Object} RouterCellProps
 * @property {string} relativePath
 * @property {boolean} [allQuery]
 * @property {{[x:string]: string | number}} [routeIds]
 */

/**
 * @type { React.ForwardRefExoticComponent<RouterCellProps & React.HTMLAttributes<HTMLDivElement> & React.RefAttributes<HTMLDivElement>>}
 */

export const RouterCell = React.forwardRef(
  (
    { children, relativePath, routeIds = {}, allQuery = true, ...props },
    ref,
  ) => {
    const router = useRouter();
    let queryValue = allQuery ? router.query : {};
    const params = removeNullUndefined({ ...queryValue, ...routeIds });
    const queryParams = new URLSearchParams(params);
    return (
      <div
        ref={ref}
        {...props}
        onClick={() => router.push(relativePath.concat('?' + queryParams))}
      >
        {children}
      </div>
    );
  },
);

export const ImageContain = styled.div`
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  &[data-image='true'] img {
    width: 250px;
  }
`;

/**
 * @typedef {object} BadgeCell
 * @property {string | React.ReactNode}  statusText
 * @property {"primary" | "success" | "danger" | "info" | "warning" | "light" | "dark" | "light-danger"|"light-secondary"|"light-secondary" | "light-warning" | "light-success" | "light-dark"} [variant]
 * @property {import('reactstrap').BadgeProps} [spaneProperty]
 */

/**
 * @type { React.ForwardRefExoticComponent<BadgeCell & React.HTMLAttributes<HTMLSpanElement> & React.RefAttributes<HTMLDivElement>>}
 */

export const BadgeStatus = React.forwardRef(
  ({ statusText, variant = 'primary', spaneProperty, ...props }, ref) => {
    return (
      <div {...props} ref={ref}>
        <Badge color={variant} {...spaneProperty}>
          {statusText}
        </Badge>
      </div>
    );
  },
);

export const selectedCetre = () => {
  const { query } = useRouter();

  const selectCentre = useSelector(getSelectedCentre);

  return query?.centreId ?? selectCentre?._id ?? query?.centre;
};

/**
 * @typedef {Object} INIT_DATA
 * @property {number} totalPage
 * @property {number} totalItems
 * @property {number} currentPage
 *
 * @typedef {"NODATA" | "IDLE" | "FORCE_TO_LOAD"} No_Data
 */
/**




/**
 * @param {object} params
 * @param { React.ReactNode } [params.children]
 * @param { React.ReactNode | string | number } params.header
* @param { string } [params.headerClassName]
 * @param { "numeric" | "alphabet" | "date" } [params.filterType]
 * @param {{name: string; Element: React.ElementType; isOptional?: boolean; fieldOptions?: {[x:string]: any}}[]} [params.filterInputs]
 * @param {{[x:string]: any }} [params.defaultValues] -if not common select there send like this {select: Equals}
 * @param {string} params.accessorKey
  * @param {boolean} [params.sort]
 * @returns {JSX.Element}
 */

export const FilterHeader = ({
  filterType = 'alphabet',
  accessorKey,
  header,
  filterInputs = [],
  defaultValues = { select: 'Equals' },
  headerClassName,
  sort = true,
}) => {
  const { replace, pathname, query } = useRouter();
  const [open, setOpen] = useState(false);

  const colorStatus = useSelector(getColor);

  const form = useForm({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: useMemo(() => defaultValues, [defaultValues]),
  });

  const filterTypeInfo = useMemo(
    () => ({
      date: {
        up: 'Sort Older to Newer',
        down: 'Sort Newer to Older',
      },
      numeric: {
        up: 'Sort Low to High',
        down: 'Sort High to Low',
      },
      alphabet: {
        up: 'Sort A to Z',
        down: 'Sort Z to A',
      },
      queryParams: query[accessorKey] ?? '',
      orderBy: query?.orderBy,
      order: query?.order,
    }),
    [query[accessorKey], query?.orderBy, query?.order],
  );

  const handleChange = async (type) => {
    const orderBy = type === 'UP' ? 'asc' : 'desc';
    const order = accessorKey;
    if (accessorKey) {
      let params = removeNullUndefined({
        ...query,
        order,
        orderBy,
      });
      setOpen(false);
      await replace({
        query: params,
      });
    }
  };

  const handleClear = () => {
    form.reset();
    let params = removeNullUndefined({
      ...query,
      order: undefined,
      orderBy: undefined,
      [accessorKey]: undefined,
    });
    replace({ query: params, pathname });
  };
  const onSubmit = async (data) => {
    try {
      let wrapper = data?.main || formatWrapper(data);

      if (!wrapper) return null;

      if (accessorKey) {
        delete query.page;
        const params = removeNullUndefined({
          ...query,
          [accessorKey]: `${wrapper}~${data.select}`,
        });
        setOpen(false);
        await replace({ query: params });
      }
    } catch (error) {
      console.log(error);
      handleToastError(error);
    }
  };

  const formatWrapper = (data) => {
    switch (true) {
      case Boolean(data?.date):
        return formatHHmmDDMMYYYY(data.date);

      case Boolean(data?.rangeDate?.length):
        const formattedRange = data.rangeDate
          .map((date) => moment(date).format('DD/MM/YYYY'))
          .join(' - ');

        return data.rangeDate.length === 1
          ? `${formattedRange} - ${formattedRange}`
          : formattedRange;
      case Boolean(data?.slider?.length):
        return data?.slider?.join('/');
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!filterTypeInfo.queryParams) {
      form.reset(defaultValues);
      return;
    }

    const fieldArray = transformedArray(
      { filter: String(filterTypeInfo.queryParams) },
      [],
    );

    if (!fieldArray.length) return;

    fieldArray.forEach(({ field, value, type }) => {
      filterInputs.forEach((input) => {
        switch (input.name) {
          case 'date':
            const formattedDate = moment(
              value[0],
              input.fieldOptions?.format,
            ).toDate();
            form.setValue(input.name, formattedDate);
            break;

          case 'rangeDate':
            const hasRangeSeparator = value[0].toString().includes(' - ');
            const parseDate = (date) => moment(date, 'DD/MM/YYYY').toDate();

            const formattedRange = hasRangeSeparator
              ? value[0].toString().split(' - ').map(parseDate)
              : value.map(parseDate);

            form.setValue(input.name, formattedRange);
            break;

          case 'main':
            if (
              input.fieldOptions?.mode === 'multiple' ||
              String(value).includes(',')
            ) {
              form.setValue(input.name, String(value).split(','));
            } else {
              form.setValue(input.name, value[0]);
            }

            break;

          case 'select':
            form.setValue(input.name, type);
            break;

          case 'slider':
            const hasRangeSlider = value[0].toString().includes('/');
            const rangeSilder = hasRangeSlider
              ? value[0].toString().split('/')
              : [0, 0];
            form.setValue(input.name, rangeSilder);
            break;

          default:
            break;
        }
      });
    });
  }, [filterTypeInfo.queryParams]);
  const menu = (
    <Menu className="width-200">
      {sort && (
        <>
          <Menu.Item onClick={() => handleChange('UP')}>
            <ArrowUp size={16} color="#B1C1EB" />{' '}
            <span className="tablefilter_title">
              {filterTypeInfo[filterType].up}
            </span>
          </Menu.Item>
          <Menu.Item onClick={() => handleChange('DOWN')}>
            <ArrowDown size={16} color="#B1C1EB" />{' '}
            <span className="tablefilter_title">
              {filterTypeInfo[filterType].down}
            </span>
          </Menu.Item>
        </>
      )}

      {!!filterInputs.length && (
        <>
          <Menu.Divider />
          <Menu.ItemGroup
            title={
              <>
                <Filter size={16} color="#B1C1EB" />{' '}
                <span className="tablefilter_title">Filter By</span>
              </>
            }
          >
            <>
              <Form onSubmit={form.handleSubmit(onSubmit)}>
                {filterInputs.map(
                  ({ Element, name, isOptional, fieldOptions }, index) => (
                    <Menu.Item key={index} className="antdropdownhandle">
                      <Controller
                        name={name}
                        control={form.control}
                        rules={{ required: isOptional }}
                        render={(fields) => (
                          <Element
                            {...fields}
                            {...fieldOptions}
                            onChange={(value) => {
                              if (fieldOptions?.onChange) {
                                const result = fieldOptions.onChange(value);
                                fields.onChange(result);
                              } else {
                                fields.onChange(value);
                              }
                            }}
                          />
                        )}
                      />
                    </Menu.Item>
                  ),
                )}
                <Menu.Divider />
                <Menu.Item>
                  <Row justify="end" className="mx-0">
                    <ThemeButton
                      padding="7px 10px"
                      type="submit"
                      className="mr-50"
                    >
                      Apply
                    </ThemeButton>
                    <ThemeButton
                      onClick={handleClear}
                      padding="7px 10px"
                      type="reset"
                      variant="secondary"
                    >
                      Clear
                    </ThemeButton>
                  </Row>
                </Menu.Item>
              </Form>
            </>
          </Menu.ItemGroup>
        </>
      )}
    </Menu>
  );
  return (
    <Dropdown
      trigger={['click']}
      overlay={menu}
      visible={open}
      onVisibleChange={setOpen}
    >
      <Row
        className={classnames(
          'view-page-title-stages mx-0 whiteSpaceNowrap cursor-pointer',
          headerClassName,
        )}
        wrap={false}
        align="middle"
      >
        {header} {/* sort filter  */}
        {!filterTypeInfo.queryParams && (
          <ChevronDown
            size={18}
            color="#646871"
            className={classnames(
              'ml-05',
              filterTypeInfo.order === accessorKey &&
                filterTypeInfo.orderBy === 'desc' &&
                'rotate-180',
            )}
          />
        )}
        {/* query filter  */}
        {filterTypeInfo.queryParams && (
          <MdFilterList size={18} color={colorStatus} className={'ml-05'} />
        )}
      </Row>
    </Dropdown>
  );
};

/**
 *
 * @param {{onOk: () => void, onClear: () => void}} param
 * @returns {JSX.Element}
 */

export const FilterButton = ({ onOk, onClear }) => {
  return (
    <Row justify="end" className="mx-0">
      <ThemeButton
        onClick={onOk}
        padding="7px 10px"
        type="button"
        className="mr-50"
      >
        Apply
      </ThemeButton>
      <ThemeButton
        onClick={onClear}
        padding="7px 10px"
        type="button"
        variant="secondary"
      >
        Clear
      </ThemeButton>
    </Row>
  );
};

export const ATAG = styled.a`
  color: #646871 !important;
  :hover {
    text-decoration: underline;
    color: blue !important;
  }
`;
